import React, { useEffect, useMemo, useRef, useState } from "react";
import { Node, OnCheckNode } from "react-checkbox-tree";
import { skipToken } from "@reduxjs/toolkit/query";

import { CheckboxTree } from "ui";

import { useGetVersionConfigQuery } from "fond/api";
import { LayerStyle } from "fond/types/ProjectLayerConfig";
import { BlockSpinner } from "fond/widgets";

import { generateLayerStyleTreeItems } from "./helper";

import { Container } from "./LayerStyleTree.styles";

interface LayerStyleTreeProps {
  versionId: string | undefined;
  excludedKeys?: string[];
  onSelectionChange: (selected: LayerStyle[]) => void;
}

const LayerStyleTree: React.FC<LayerStyleTreeProps> = ({ versionId, excludedKeys, onSelectionChange }: LayerStyleTreeProps) => {
  const { data: config, isLoading, isFetching } = useGetVersionConfigQuery(versionId ?? skipToken, { skip: !versionId });
  const [checked, setChecked] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const treeRef = useRef<CheckboxTree>(null);

  const treeItems = useMemo(() => {
    return config ? generateLayerStyleTreeItems({ config, excludedKeys: excludedKeys || [] }) : [];
  }, [config, excludedKeys]);

  // This effect is used to expand all nodes when the config changes
  useEffect(() => {
    treeRef.current?.onExpandAll();
  }, [config, excludedKeys]);

  // Check (resp. uncheck) the descendants of a checked (resp. unchecked) node
  const handleOnCheck = (checkedItems: string[], onCheckNode: OnCheckNode) => {
    if (!config) return;

    const descendants: string[] = [];
    const traverse = (node: Node) => {
      descendants.push(node.value);
      node.children?.forEach((child) => {
        traverse(child);
      });
    };
    traverse(onCheckNode);

    const newChecked = onCheckNode.checked
      ? [...new Set([...checkedItems, ...descendants])]
      : checkedItems.filter((value) => !descendants.includes(value));

    setChecked(newChecked);

    const selectedStyles = newChecked.map((styleId) => config.Data.entities[styleId]).filter((entity) => entity?.Type === "STYLE") as LayerStyle[];
    onSelectionChange(selectedStyles);
  };

  if (isLoading || isFetching) return <BlockSpinner containerProps={{ height: "100%" }} />;

  return (
    <Container data-test-id="layer-style-tree">
      <CheckboxTree ref={treeRef} checked={checked} expanded={expanded} onCheck={handleOnCheck} onExpand={setExpanded} nodes={treeItems} noCascade />
    </Container>
  );
};

export default LayerStyleTree;
